import React, { Fragment, useContext } from "react";
import { MediaContext } from "../../common/media-context";
import {
  useDocumentMediumFormats,
  useImageMediumFormats,
} from "../../vocabularyServices";
import { includes } from "ramda";
import { notBlank } from "../../../util/ext";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";

import AllowDownloadCheckbox from "../../content/edit/AllowDownloadCheckbox";
import AudioTypeSelect from "../../content/edit/AudioTypeSelect";
import LanguageSelect from "../../content/edit/LanguageSelect";
import LicenseTypeSelect from "../../content/edit/LicenseTypeSelect";
import MediumFormatSelect from "../../content/edit/MediumFormatSelect";
import UploadColumn from "./UploadColumn";
import PublicationDate from "../../common/PublicationDate";

const SubmissionInfo = props => {
  const { className } = props;
  const context = useContext(MediaContext);

  const documentMediumFormats = useDocumentMediumFormats();
  const imageMediumFormats = useImageMediumFormats();

  const isRequired = field => {
    const { type } = context.media;
    return (
      notBlank(context.requiredFields[type]) &&
      includes(field, context.requiredFields[type])
    );
  };

  const handleBlur = field => event => {
    const value = context.media[field];

    context.setErrors(prev =>
      context.validator.validateField(context.media.type, field, value, prev),
    );
  };

  const formBuilder = handlers => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <LanguageSelect
            required={isRequired("language")}
            value={context.media.language}
            onChange={handlers.onAutocompleteChange("isoCode639_2", [
              "language",
            ])}
            onBlur={handleBlur("language")}
            error={context.validator.hasError(context.errors, "language")}
            helperText={context.validator.firstError(
              context.errors,
              "language",
            )}
          />
        </Grid>

        {context.media.type === "ARTICLE" && (
          <Fragment>
            <Grid item xs={12}>
              <TextField
                id={`content-upload-periodical`}
                fullWidth
                required={isRequired("periodical")}
                label="Periodical"
                onChange={handlers.onTextChange(["periodical"])}
                onBlur={handleBlur("periodical")}
                error={context.validator.hasError(context.errors, "periodical")}
                helperText={context.validator.firstError(
                  context.errors,
                  "periodical",
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id={`content-upload-periodical-volume`}
                fullWidth
                required={isRequired("periodicalVolume")}
                label="Periodical Volume"
                onChange={handlers.onTextChange(["periodicalVolume"])}
                onBlur={handleBlur("periodicalVolume")}
                error={context.validator.hasError(
                  context.errors,
                  "periodicalVolume",
                )}
                helperText={context.validator.firstError(
                  context.errors,
                  "periodicalVolume",
                )}
              />
            </Grid>
          </Fragment>
        )}

        {(context.media.type === "OTHER_DOCUMENT" ||
          context.media.type === "IMAGE") && (
          <Grid item xs={12}>
            <MediumFormatSelect
              required={isRequired("mediumFormat")}
              valueFormat="mediumFormat"
              value={context.media.mediumFormat}
              onChange={handlers.onAutocompleteChange(null, ["mediumFormat"])}
              mediumFormats={
                context.media.type === "OTHER_DOCUMENT"
                  ? documentMediumFormats
                  : imageMediumFormats
              }
              onBlur={handleBlur("mediumFormat")}
              error={context.validator.hasError(context.errors, "mediumFormat")}
              helperText={context.validator.firstError(
                context.errors,
                "mediumFormat",
              )}
            />
          </Grid>
        )}

        {context.media.type === "AUDIO" && (
          <Grid item xs={12}>
            <AudioTypeSelect
              required={isRequired("audioType")}
              valueFormat="audioType"
              value={context.media.audioType}
              onChange={handlers.onTextChange(["audioType"])}
              onBlur={handleBlur("audioType")}
              error={context.validator.hasError(context.errors, "audioType")}
              helperText={context.validator.firstError(
                context.errors,
                "audioType",
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} container spacing={2} style={{ paddingRight: 0 }}>
          <PublicationDate
            errors={context.errors}
            handleBlur={handleBlur}
            isRequired={isRequired}
            media={context.media}
            onCheckboxChange={handlers.onCheckboxChange}
            onDateChange={handlers.onDateChange}
            onTextChange={handlers.onTextChange}
            validator={context.validator}
          />
        </Grid>

        <Grid container item xs={12} direction="column">
          <LicenseTypeSelect
            required
            value={context.media.licenseType}
            onChange={handlers.onAutocompleteChange("id", ["licenseType"])}
            onBlur={handleBlur("licenseType")}
            error={context.validator.hasError(context.errors, "licenseType")}
            helperText={context.validator.firstError(
              context.errors,
              "licenseType",
            )}
          />
          <AllowDownloadCheckbox
            licenseTypeError={context.validator.hasError(
              context.errors,
              "licenseType",
            )}
            value={context.media.allowDownload}
            onChange={handlers.onCheckboxChange("allowDownload")}
          />
        </Grid>

        {includes(context.media.type, ["AUDIO", "VIDEO"]) && (
          <Grid item xs={12}>
            <TextField
              required={isRequired("keywords")}
              label="Keywords"
              fullWidth
              value={context.media.keywords}
              error={context.validator.hasError(context.errors, "keywords")}
              helperText={
                context.validator.hasError(context.errors, "keywords")
                  ? context.validator.firstError(context.errors, "keywords")
                  : "Keywords should be separated by commas"
              }
              onChange={handlers.onTextChange(["keywords"])}
              onBlur={handleBlur("keywords")}
            />
          </Grid>
        )}

        {context.media.type === "ARTICLE" && (
          <Grid item xs={12}>
            <TextField
              label="Volume (optional)"
              fullWidth
              inputProps={{
                maxLength: 100,
              }}
              value={context.media.volume}
              onChange={handlers.onTextChange(["volume"])}
            />
          </Grid>
        )}

        {includes(context.media.type, ["AUDIOBOOK", "BOOK"]) && (
          <Fragment>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Volume (optional)"
                  fullWidth
                  inputProps={{
                    maxLength: 100,
                  }}
                  value={context.media.volume}
                  onChange={handlers.onTextChange(["volume"])}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Edition (optional)"
                  fullWidth
                  inputProps={{
                    maxLength: 100,
                  }}
                  value={context.media.edition}
                  onChange={handlers.onTextChange(["edition"])}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="ISBN (optional)"
                fullWidth
                inputProps={{
                  maxLength: 13,
                }}
                onChange={handlers.onTextChange(["isbn"])}
              />
            </Grid>
          </Fragment>
        )}

        {context.media.type === "VIDEO" && (
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Series (optional)"
                fullWidth
                inputProps={{
                  maxLength: 255,
                }}
                value={context.media.series}
                onChange={handlers.onTextChange(["series"])}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Episode (optional)"
                fullWidth
                inputProps={{
                  maxLength: 255,
                }}
                value={context.media.episode}
                onChange={handlers.onTextChange(["episode"])}
              />
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            label="Publisher (optional)"
            fullWidth
            inputProps={{
              maxLength: 255,
            }}
            value={context.media.publisher}
            onChange={handlers.onTextChange(["publisher"])}
          />
        </Grid>
      </Grid>
    );
  };

  return <UploadColumn className={className} formBuilder={formBuilder} />;
};

export default SubmissionInfo;
